import AddIcon from "@mui/icons-material/Add";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { Box, CardHeader, Divider, Stack, Typography } from "@mui/material";
import MuiCard from "@mui/material/Card";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DeleteButton } from "../../shared/FormLayout/IconButtons/DeleteButton";
import { convertReusableQuestionGroupToWorkflowMetadataGroup } from "../../shared/helpers/CustomQuestionGroupUtils";
import { ReusableCustomQuestionGroup } from "../../shared/interfaces/ReusableCustomQuestionGroup";
import MenuButton from "../../shared/MenuButton";
import { ValidationMessage } from "../../shared/ValidationMessage";
import { CustomQuestionsList } from "../CustomQuestionsList";
import { ReusableQuestionGroupSelectionDialog } from "../ReusableCustomQuestions/QuestionGroups/ReusableQuestionGroupSelectionDialog";
import { CustomQuestionGroup, WorkflowMetadataGroup, CustomQuestionField } from "../../shared/interfaces/Workflow";

interface Props {
  customerId: number;
  editMode?: boolean;
  customQuestionGroups: CustomQuestionGroup[];
  onCustomQuestionGroupNameChange: (groupId: string, groupName: string) => void;
  onCustomQuestionGroupRemoveClick: (groupId: string) => void;
  onCustomQuestionGroupAddClick: (questionGroup?: WorkflowMetadataGroup) => void;
  onQuestionFieldAddClick?: (groupId: string, questionField: CustomQuestionField) => void;
  onQuestionFieldEditClick?: (groupId: string, questionField: CustomQuestionField) => void;
  onQuestionFieldRemoveClick?: (groupId: string, fieldId: string) => void;
}

export function CustomQuestions(props: Props) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    unregister,
  } = useFormContext();
  const {
    customerId,
    editMode,
    customQuestionGroups,
    onCustomQuestionGroupNameChange,
    onCustomQuestionGroupRemoveClick,
    onCustomQuestionGroupAddClick,
    onQuestionFieldAddClick,
    onQuestionFieldEditClick,
    onQuestionFieldRemoveClick,
  } = props;

  const [librarySelectionDialogOpen, setLibrarySelectionDialogOpen] = useState(false);

  function handleAddGroupFromLibraryButtonClick() {
    setLibrarySelectionDialogOpen(true);
  }

  function handleConfirmAddGroupFromLibraryClick(selectedGroups: ReusableCustomQuestionGroup[]) {
    selectedGroups.forEach((selectedGroup) => {
      const workflowMetadataGroup = convertReusableQuestionGroupToWorkflowMetadataGroup(selectedGroup);
      onCustomQuestionGroupAddClick(workflowMetadataGroup);
    });
  }

  function handleDeleteGroupClick(id: string) {
    unregister(`substationTab.customQuestionGroups.${id}`);
    onCustomQuestionGroupRemoveClick(id);
  }

  return (
    <>
      {customQuestionGroups && customQuestionGroups.length > 0 ? (
        customQuestionGroups.map((questionGroup) => {
          const questionGroupErrors = errors?.substationTab?.customQuestionGroups?.[questionGroup.id];

          return (
            <MuiCard key={`container-${questionGroup.id}`} variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="subtitle2" color="textSecondary" lineHeight="3.3em">
                      {t("customQuestions.questionGroup")}:
                    </Typography>
                    {editMode ? (
                      <Stack>
                        <InputText
                          placeholder={t("customQuestions.questionGroupName")}
                          className={questionGroupErrors?.groupName ? "p-invalid" : undefined}
                          {...register(`substationTab.customQuestionGroups.${questionGroup.id}.groupName`, {
                            required: true,
                            value: questionGroup.name,
                            onChange: (e) => onCustomQuestionGroupNameChange(questionGroup.id, e.target.value),
                          })}
                        />
                        {questionGroupErrors?.groupName?.type === "required" && (
                          <ValidationMessage
                            invalidMessage={t("customQuestions.customQuestionGroupNameEmptyValidation")}
                            validationType="error"
                            showIcon
                          />
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="subtitle2">{questionGroup.name}</Typography>
                    )}
                  </Stack>
                }
                action={
                  editMode && (
                    <DeleteButton
                      onClick={() => handleDeleteGroupClick(questionGroup.id)}
                      tooltipText={t("customQuestions.deleteQuestionGroup")}
                    />
                  )
                }
              />
              <Divider />
              <Box mt={2}>
                <CustomQuestionsList
                  customerId={customerId}
                  editMode={editMode}
                  groupId={questionGroup.id}
                  groupFields={questionGroup.fields}
                  onQuestionFieldAdd={onQuestionFieldAddClick}
                  onQuestionFieldEdit={onQuestionFieldEditClick}
                  onQuestionFieldRemove={onQuestionFieldRemoveClick}
                />
              </Box>
            </MuiCard>
          );
        })
      ) : (
        <Box display="flex" justifyContent="center" my={2}>
          <Typography variant="caption" noWrap>
            {t("customQuestions.customQuestionsEmpty")}
          </Typography>
        </Box>
      )}
      {editMode && (
        <Box display="flex" justifyContent="center">
          <MenuButton
            variant="button"
            buttonProps={{
              color: "primary",
              variant: "text",
              sx: { textTransform: "uppercase" },
            }}
            tooltipText={t("customQuestions.addQuestion")}
            menuItems={[
              {
                icon: <LibraryAddIcon />,
                text: t("customQuestionLibrary.addFromQuestionLibrary"),
                onClick: handleAddGroupFromLibraryButtonClick,
              },
              {
                icon: <AddIcon />,
                text: t("customQuestions.addNewQuestionGroup"),
                onClick: onCustomQuestionGroupAddClick,
              },
            ]}
          >
            {t("customQuestions.addQuestionGroup")}
          </MenuButton>
        </Box>
      )}
      {librarySelectionDialogOpen && (
        <ReusableQuestionGroupSelectionDialog
          customerId={props.customerId}
          onApplySelection={handleConfirmAddGroupFromLibraryClick}
          open={librarySelectionDialogOpen}
          onClose={() => setLibrarySelectionDialogOpen(false)}
        />
      )}
    </>
  );
}
